// lib/mirelo-sfx.ts
import type { Layer } from '#types/types';
import {
  TIMELINE_AUDIO_GAIN_PARAM_KEY,
  TIMELINE_AUDIO_ONLY_PARAM_KEY,
  TIMELINE_MUTE_AUDIO_PARAM_KEY,
  TIMELINE_SEPARATED_FROM_LAYER_ID_PARAM_KEY,
} from '#lib/workspace-timeline';

const MIRELO_SFX_MODEL_ID = 'mirelo-sfx-1.6';
export const MIRELO_SFX_FAL_ENDPOINT = 'mirelo-ai/sfx1.6/video-to-video';
export const MIRELO_SFX_MIN_DURATION_SECONDS = 1;
export const MIRELO_SFX_MAX_DURATION_SECONDS = 60;

export type MireloSfxFalInput = {
  video_url: string;
  duration: number;
  num_samples: 1;
  text_prompt?: string;
  seed?: number;
};

export const buildMireloSfxFalInput = (args: {
  videoUrl: string;
  durationSeconds: number;
  prompt?: string | null;
  seed?: number | null;
}): MireloSfxFalInput => {
  const videoUrl = args.videoUrl.trim();
  if (!videoUrl) throw new Error('Missing source video for Mirelo SFX 1.6.');
  if (
    !Number.isFinite(args.durationSeconds) ||
    args.durationSeconds < MIRELO_SFX_MIN_DURATION_SECONDS ||
    args.durationSeconds > MIRELO_SFX_MAX_DURATION_SECONDS
  ) {
    throw new Error('Bad request: Mirelo SFX source duration must be between 1 and 60 seconds.');
  }

  const prompt = typeof args.prompt === 'string' ? args.prompt.trim() : '';
  const seed = Number(args.seed);
  return {
    video_url: videoUrl,
    duration: Math.round(args.durationSeconds * 1000) / 1000,
    num_samples: 1,
    ...(prompt ? { text_prompt: prompt } : {}),
    ...(Number.isInteger(seed) && seed >= 0 ? { seed } : {}),
  };
};

export type MireloTimelineTrackLike = {
  layerId: string;
  layerType: 'image' | 'video' | 'audio';
  startMs: number;
  endMs: number;
  renderOrder: number;
  visible?: boolean;
};

export type MireloTimelineInput = {
  startMs: number;
  endMs: number;
  durationSeconds: number;
  source: 'range' | 'selected-track' | 'cursor-track';
};

const finiteNonNegativeMs = (value: unknown): number | null => (
  typeof value === 'number' && Number.isFinite(value) && value >= 0
    ? Math.round(value)
    : null
);

export const resolveMireloTimelineInput = (args: {
  videoInputStartMs?: number | null;
  videoInputEndMs?: number | null;
  cursorMs: number;
  timelineEndMs: number;
  tracks: readonly MireloTimelineTrackLike[];
  selectedTrackLayerIds?: readonly string[];
}): MireloTimelineInput | null => {
  const tracks = args.tracks.filter((track) => (
    track.visible !== false && track.endMs > track.startMs
  ));
  const videoTracks = tracks.filter((track) => track.layerType === 'video');
  const explicitStartMs = finiteNonNegativeMs(args.videoInputStartMs);
  const explicitEndMs = finiteNonNegativeMs(args.videoInputEndMs);
  const hasExplicitRange = explicitStartMs !== null || explicitEndMs !== null;

  if (hasExplicitRange) {
    const startMs = explicitStartMs ?? 0;
    const endMs = explicitEndMs ?? Math.max(0, Math.round(args.timelineEndMs));
    if (endMs <= startMs) return null;
    if (!videoTracks.some((track) => track.startMs < endMs && track.endMs > startMs)) return null;
    return {
      startMs,
      endMs,
      durationSeconds: (endMs - startMs) / 1000,
      source: 'range',
    };
  }

  const selectedIds = new Set(
    (args.selectedTrackLayerIds ?? []).filter((layerId) => typeof layerId === 'string' && layerId.length > 0),
  );
  const selectedTracks = tracks.filter((track) => selectedIds.has(track.layerId));
  if (selectedTracks.length > 1) return null;
  if (selectedTracks.length === 1) {
    const selectedTrack = selectedTracks[0];
    if (!selectedTrack || selectedTrack.layerType !== 'video') return null;
    return {
      startMs: selectedTrack.startMs,
      endMs: selectedTrack.endMs,
      durationSeconds: (selectedTrack.endMs - selectedTrack.startMs) / 1000,
      source: 'selected-track',
    };
  }

  const cursorMs = Math.max(0, Math.round(args.cursorMs));
  const cursorTrack = videoTracks
    .filter((track) => cursorMs >= track.startMs && cursorMs < track.endMs)
    .sort((left, right) => {
      if (left.renderOrder !== right.renderOrder) return left.renderOrder - right.renderOrder;
      return left.layerId.localeCompare(right.layerId);
    })
    .at(-1);
  if (!cursorTrack) return null;
  return {
    startMs: cursorTrack.startMs,
    endMs: cursorTrack.endMs,
    durationSeconds: (cursorTrack.endMs - cursorTrack.startMs) / 1000,
    source: 'cursor-track',
  };
};

export const buildMireloRecoveryCarrier = (carrierLayer: Layer): Layer => ({
  ...carrierLayer,
  name: 'Mirelo SFX carrier (separation required)',
  visible: true,
});

export const buildMireloSeparatedLayers = (args: {
  carrierLayer: Layer;
  audioSrc: string;
  audioLayerId: string;
  timestamp: number;
}): { carrierLayer: Layer; audioLayer: Layer } => {
  const carrierParams = args.carrierLayer.params && typeof args.carrierLayer.params === 'object'
    ? args.carrierLayer.params
    : {};
  const carrierLayer: Layer = {
    ...args.carrierLayer,
    visible: false,
    params: {
      ...carrierParams,
      [TIMELINE_MUTE_AUDIO_PARAM_KEY]: true,
    },
  };
  const audioLayer: Layer = {
    ...args.carrierLayer,
    id: args.audioLayerId,
    timestamp: args.timestamp,
    name: 'Mirelo SFX',
    type: 'audio',
    source: MIRELO_SFX_MODEL_ID,
    visible: true,
    videoSrc: args.audioSrc,
    videoFramesUrls: [],
    imageSrc: undefined,
    imageData: null,
    params: {
      ...carrierParams,
      [TIMELINE_AUDIO_ONLY_PARAM_KEY]: true,
      [TIMELINE_MUTE_AUDIO_PARAM_KEY]: false,
      [TIMELINE_AUDIO_GAIN_PARAM_KEY]: 1,
      [TIMELINE_SEPARATED_FROM_LAYER_ID_PARAM_KEY]: args.carrierLayer.id,
    },
  };
  return { carrierLayer, audioLayer };
};
